// HKDSE 2024 Paper 1 Knowledge Graph Samples
// Organized by question types with systematic naming

export const hkdse2024Samples = {
  algebra: [
    {
      id: 'hkdse2024_algebra_01',
      problem: "化簡 $\\frac{2}{4h-7} - \\frac{3}{6h-5}$。",
      problemType: "algebra_fractions",
      difficulty: "intermediate",
      description: "Simplifying algebraic fractions",
      graphData: {
        nodes: [
          {
            id: "fraction_subtraction",
            name: "Fraction Subtraction",
            type: "concepts",
            description: "Subtracting two fractions with different denominators",
            value: "2/(4h-7) - 3/(6h-5)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "common_denominator",
            name: "Common Denominator",
            type: "concepts",
            description: "Find least common multiple of denominators",
            value: "LCM of (4h-7) and (6h-5)",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_find_lcm",
            name: "Step 1: Find LCM",
            type: "steps",
            description: "Find least common multiple",
            value: "LCM = (4h-7)(6h-5)",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_rewrite_fractions",
            name: "Step 2: Rewrite Fractions",
            type: "steps",
            description: "Rewrite with common denominator",
            value: "2(6h-5)/[(4h-7)(6h-5)] - 3(4h-7)/[(4h-7)(6h-5)]",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_expand_numerators",
            name: "Step 3: Expand Numerators",
            type: "steps",
            description: "Expand the numerators",
            value: "(12h-10)/[(4h-7)(6h-5)] - (12h-21)/[(4h-7)(6h-5)]",
            importance: 8,
            x: 0, y: 100, z: -15
          },
          {
            id: "step4_combine",
            name: "Step 4: Combine",
            type: "steps",
            description: "Combine the fractions",
            value: "(12h-10-12h+21)/[(4h-7)(6h-5)]",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "step5_simplify",
            name: "Step 5: Simplify",
            type: "steps",
            description: "Simplify the numerator",
            value: "11/[(4h-7)(6h-5)]",
            importance: 9,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The simplified fraction",
            value: "11/[(4h-7)(6h-5)]",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "fraction_subtraction", to: "common_denominator", type: "requires", strength: 9 },
          { from: "common_denominator", to: "step1_find_lcm", type: "enables", strength: 8 },
          { from: "step1_find_lcm", to: "step2_rewrite_fractions", type: "enables", strength: 9 },
          { from: "step2_rewrite_fractions", to: "step3_expand_numerators", type: "leads to", strength: 8 },
          { from: "step3_expand_numerators", to: "step4_combine", type: "leads to", strength: 8 },
          { from: "step4_combine", to: "step5_simplify", type: "leads to", strength: 9 },
          { from: "step5_simplify", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'hkdse2024_algebra_02',
      problem: "令 $x$ 成為公式 $\\frac{Ax+C}{B} = 3x$ 的主項。",
      problemType: "algebra_formula",
      difficulty: "intermediate",
      description: "Rearranging formula to make a variable the subject",
      graphData: {
        nodes: [
          {
            id: "fraction_equation",
            name: "Fraction Equation",
            type: "concepts",
            description: "Equation with fraction involving variable x",
            value: "(Ax+C)/B = 3x",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cross_multiplication",
            name: "Cross Multiplication",
            type: "concepts",
            description: "Method to eliminate fraction by multiplying both sides",
            value: "If a/b = c, then a = bc",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_cross_multiply",
            name: "Step 1: Cross Multiply",
            type: "steps",
            description: "Multiply both sides by B to eliminate fraction",
            value: "Ax + C = 3Bx",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_collect_terms",
            name: "Step 2: Collect Terms",
            type: "steps",
            description: "Collect x terms on one side",
            value: "Ax - 3Bx = -C",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_factor_x",
            name: "Step 3: Factor x",
            type: "steps",
            description: "Factor out x from left side",
            value: "x(A - 3B) = -C",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "step4_solve_x",
            name: "Step 4: Solve for x",
            type: "steps",
            description: "Divide both sides by (A - 3B)",
            value: "x = -C/(A - 3B)",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "x expressed in terms of A, B, and C",
            value: "x = -C/(A - 3B)",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "fraction_equation", to: "cross_multiplication", type: "requires", strength: 9 },
          { from: "cross_multiplication", to: "step1_cross_multiply", type: "enables", strength: 10 },
          { from: "step1_cross_multiply", to: "step2_collect_terms", type: "leads to", strength: 8 },
          { from: "step2_collect_terms", to: "step3_factor_x", type: "leads to", strength: 9 },
          { from: "step3_factor_x", to: "step4_solve_x", type: "leads to", strength: 10 },
          { from: "step4_solve_x", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  factorization: [
    {
      id: 'hkdse2024_factorization_01',
      problem: "因式分解 (a) $6r^2 - 13rs - 28s^2$，(b) $4r - 14s + 6r^2 - 13rs - 28s^2$。",
      problemType: "factorization",
      difficulty: "intermediate",
      description: "Factorization of quadratic expressions",
      graphData: {
        nodes: [
          {
            id: "quadratic_expression",
            name: "Quadratic Expression",
            type: "concepts",
            description: "Expression with r² and s² terms",
            value: "6r² - 13rs - 28s²",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "ac_method",
            name: "AC Method",
            type: "concepts",
            description: "Method for factoring ax² + bx + c",
            value: "Find factors of ac that add to b",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_find_ac",
            name: "Step 1: Find AC",
            type: "steps",
            description: "Calculate a × c = 6 × (-28) = -168",
            value: "AC = 6 × (-28) = -168",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_find_factors",
            name: "Step 2: Find Factors",
            type: "steps",
            description: "Find factors of -168 that add to -13",
            value: "Factors: 8 and -21 (8 × -21 = -168, 8 + -21 = -13)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_split_middle",
            name: "Step 3: Split Middle Term",
            type: "steps",
            description: "Rewrite as 6r² + 8rs - 21rs - 28s²",
            value: "6r² + 8rs - 21rs - 28s²",
            importance: 8,
            x: 0, y: 100, z: -15
          },
          {
            id: "step4_group_factor",
            name: "Step 4: Group and Factor",
            type: "steps",
            description: "Factor by grouping",
            value: "(6r² + 8rs) + (-21rs - 28s²) = 2r(3r + 4s) - 7s(3r + 4s)",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer_a",
            name: "Answer (a)",
            type: "values",
            description: "Final factored form",
            value: "(3r + 4s)(2r - 7s)",
            importance: 10,
            x: 0, y: 300, z: 0
          },
          {
            id: "part_b_rearrange",
            name: "Part (b): Rearrange",
            type: "steps",
            description: "Rearrange terms to match part (a)",
            value: "4r - 14s + 6r² - 13rs - 28s² = 6r² - 13rs - 28s² + 4r - 14s",
            importance: 8,
            x: -200, y: 150, z: 0
          },
          {
            id: "part_b_factor",
            name: "Part (b): Factor",
            type: "steps",
            description: "Factor out common terms",
            value: "(3r + 4s)(2r - 7s) + 2(2r - 7s)",
            importance: 9,
            x: 200, y: 150, z: 0
          },
          {
            id: "final_answer_b",
            name: "Answer (b)",
            type: "values",
            description: "Final factored form for part (b)",
            value: "(2r - 7s)(3r + 4s + 2)",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "quadratic_expression", to: "ac_method", type: "requires", strength: 9 },
          { from: "ac_method", to: "step1_find_ac", type: "enables", strength: 8 },
          { from: "step1_find_ac", to: "step2_find_factors", type: "leads to", strength: 9 },
          { from: "step2_find_factors", to: "step3_split_middle", type: "enables", strength: 8 },
          { from: "step3_split_middle", to: "step4_group_factor", type: "leads to", strength: 9 },
          { from: "step4_group_factor", to: "final_answer_a", type: "equals", strength: 10 },
          { from: "final_answer_a", to: "part_b_rearrange", type: "enables", strength: 8 },
          { from: "part_b_rearrange", to: "part_b_factor", type: "leads to", strength: 9 },
          { from: "part_b_factor", to: "final_answer_b", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  inequalities: [
    {
      id: 'hkdse2024_inequalities_01',
      problem: "(a) 求同時滿足 $\\frac{5x+7}{4} - 1 < 2x$ 及 $3x + 9 \\geq 0$ 的 $x$ 值的範圍。(b) 寫出同時滿足 (a) 的不等式的最小整數。",
      problemType: "inequalities_compound",
      difficulty: "intermediate",
      description: "Solving compound inequalities",
      graphData: {
        nodes: [
          {
            id: "compound_inequalities",
            name: "Compound Inequalities",
            type: "concepts",
            description: "Two inequalities that must both be satisfied",
            value: "Inequality 1 AND Inequality 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "inequality1",
            name: "Inequality 1",
            type: "concepts",
            description: "First inequality to solve",
            value: "(5x+7)/4 - 1 < 2x",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "inequality2",
            name: "Inequality 2",
            type: "concepts",
            description: "Second inequality to solve",
            value: "3x + 9 ≥ 0",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_solve_ineq1",
            name: "Step 1: Solve Inequality 1",
            type: "steps",
            description: "Solve (5x+7)/4 - 1 < 2x",
            value: "(5x+7)/4 - 1 < 2x",
            importance: 9,
            x: -200, y: 0, z: 25
          },
          {
            id: "step1a_clear_fraction",
            name: "Step 1a: Clear Fraction",
            type: "steps",
            description: "Multiply both sides by 4",
            value: "5x + 7 - 4 < 8x",
            importance: 8,
            x: -200, y: 50, z: 0
          },
          {
            id: "step1b_simplify",
            name: "Step 1b: Simplify",
            type: "steps",
            description: "Simplify the left side",
            value: "5x + 3 < 8x",
            importance: 8,
            x: -200, y: 100, z: 0
          },
          {
            id: "step1c_solve",
            name: "Step 1c: Solve for x",
            type: "steps",
            description: "Solve the inequality",
            value: "3 < 3x → x > 1",
            importance: 9,
            x: -200, y: 150, z: 0
          },
          {
            id: "step2_solve_ineq2",
            name: "Step 2: Solve Inequality 2",
            type: "steps",
            description: "Solve 3x + 9 ≥ 0",
            value: "3x ≥ -9 → x ≥ -3",
            importance: 8,
            x: 200, y: 0, z: 25
          },
          {
            id: "step3_combine",
            name: "Step 3: Combine Solutions",
            type: "steps",
            description: "Find intersection of both solutions",
            value: "x > 1 AND x ≥ -3",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "part_a_final_range",
            name: "Part (a): Final Range",
            type: "steps",
            description: "Simplify the combined solution",
            value: "x > 1",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_b_min_integer",
            name: "Part (b): Minimum Integer",
            type: "steps",
            description: "Find smallest integer satisfying x > 1",
            value: "Smallest integer > 1 is 2",
            importance: 9,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "Range and minimum integer",
            value: "(a) x > 1 (b) 2",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "compound_inequalities", to: "inequality1", type: "includes", strength: 9 },
          { from: "compound_inequalities", to: "inequality2", type: "includes", strength: 9 },
          { from: "inequality1", to: "step1_solve_ineq1", type: "requires", strength: 9 },
          { from: "step1_solve_ineq1", to: "step1a_clear_fraction", type: "leads to", strength: 8 },
          { from: "step1a_clear_fraction", to: "step1b_simplify", type: "leads to", strength: 8 },
          { from: "step1b_simplify", to: "step1c_solve", type: "leads to", strength: 9 },
          { from: "inequality2", to: "step2_solve_ineq2", type: "requires", strength: 8 },
          { from: "step1c_solve", to: "step3_combine", type: "enables", strength: 9 },
          { from: "step2_solve_ineq2", to: "step3_combine", type: "enables", strength: 9 },
          { from: "step3_combine", to: "part_a_final_range", type: "leads to", strength: 9 },
          { from: "part_a_final_range", to: "part_b_min_integer", type: "enables", strength: 9 },
          { from: "part_a_final_range", to: "final_answers", type: "contributes to", strength: 9 },
          { from: "part_b_min_integer", to: "final_answers", type: "contributes to", strength: 9 }
        ]
      }
    }
  ],
  
  word_problems: [
    {
      id: 'hkdse2024_word_problem_01',
      problem: "某計算機的標價較其成本高 $40\\%$。該計算機以其標價七五折售出並獲利 $\\$13$。求該計算機的標價。",
      problemType: "word_problem_percentage_profit",
      difficulty: "intermediate",
      description: "Solving percentage and profit problems",
      graphData: {
        nodes: [
          {
            id: "cost_price",
            name: "Cost Price",
            type: "concepts",
            description: "Let cost price be x",
            value: "CP = x",
            importance: 8,
            x: -200, y: -200, z: 0
          },
          {
            id: "marked_price",
            name: "Marked Price",
            type: "concepts",
            description: "Marked price is 40% more than cost",
            value: "MP = 1.4x",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "selling_price",
            name: "Selling Price",
            type: "concepts",
            description: "Sold at 75% of marked price",
            value: "SP = 0.75 × MP",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "profit_amount",
            name: "Profit Amount",
            type: "concepts",
            description: "Profit is $13",
            value: "SP - CP = 13",
            importance: 10,
            x: 0, y: 0, z: 0
          },
          {
            id: "step1_express_sp",
            name: "Step 1: Express SP in terms of x",
            type: "steps",
            description: "SP = 0.75 × 1.4x",
            value: "SP = 1.05x",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "step2_set_equation",
            name: "Step 2: Set up equation",
            type: "steps",
            description: "1.05x - x = 13",
            value: "0.05x = 13",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step3_solve",
            name: "Step 3: Solve for x",
            type: "steps",
            description: "Solve the linear equation",
            value: "x = 260",
            importance: 9,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Marked price of the calculator",
            value: "MP = 1.4 × 260 = $364",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "cost_price", to: "marked_price", type: "enables", strength: 9 },
          { from: "marked_price", to: "selling_price", type: "enables", strength: 9 },
          { from: "profit_amount", to: "step2_set_equation", type: "enables", strength: 10 },
          { from: "selling_price", to: "step1_express_sp", type: "leads to", strength: 10 },
          { from: "step1_express_sp", to: "step2_set_equation", type: "leads to", strength: 10 },
          { from: "step2_set_equation", to: "step3_solve", type: "leads to", strength: 10 },
          { from: "step3_solve", to: "final_answer", type: "leads to", strength: 10 }
        ]
      }
    }
  ]
}; 